<script lang="ts">
  import VirtualList from 'svelte-tiny-virtual-list';
  import StaticWord from './StaticWord.svelte';
  import { dims } from '~/utils/dimensions.svelte';
  import ScrollShadow from './ScrollShadow.svelte';
  import { isClassic, type WordSort } from '~/types';
  import { wordsQuery } from '~/store/game.svelte';
  import InfiniteLoading, { type InfiniteEvent } from 'svelte-infinite-loading';
  import Spinner from '~/components/Spinner.svelte';
  import { getGame } from '~/store/GameContext.svelte';
  import { timeSince } from '~/utils/dates';
  import { settings } from '~/modals/Settings.svelte';

  type Props = {
    open: boolean;
    sort: WordSort;
    scroll: number;
  };
  const props: Props = $props();
  const game = $derived(getGame().current);

  const peeking = $derived(props.scroll > 0);

  // created once; option changes (sort, new words, peeking) switch the
  // observer to the matching cache entry instead of recreating it
  const wordsQ = wordsQuery(() => ({
    game,
    sort: props.sort,
    enabled: peeking,
  }));

  let height = $state(0);

  const showTimestamps = $derived(settings.wordTimestamps.current);

  // tick relative timestamps while the list is open
  let now = $state(Date.now());
  $effect(() => {
    if (!peeking || !showTimestamps) return;
    now = Date.now();
    const id = setInterval(() => (now = Date.now()), 30_000);
    return () => clearInterval(id);
  });

  const handleInfinite = async (e: InfiniteEvent) => {
    const res = await wordsQ.fetchNextPage();
    if (res.hasNextPage) {
      e.detail.loaded();
    } else {
      e.detail.complete();
    }
  };

  const sizes: Record<string, number> = {
    small: 48,
    medium: 56,
    large: 72,
  };

  const words = $derived(
    wordsQ.data?.pages.flat().slice(props.sort === 'index' ? 1 : 0) ?? [],
  );
</script>

<div class="container" style="--pad: {dims.pad}px" bind:clientHeight={height}>
  {#key `${game.id}:${props.sort}:${game.words}:${peeking}`}
    {#if peeking}
      <ScrollShadow>
        <VirtualList
          width="100%"
          {height}
          itemCount={words.length}
          itemSize={sizes[dims.size]}
          overscanCount={10}
        >
          {#snippet item({ style, index })}
            {@const word = words[index] ?? { word: [] }}
            <div class="item-container" {style}>
              <StaticWord
                {word}
                time={showTimestamps && word.timestamp
                  ? timeSince(word.timestamp, now)
                  : undefined}
                highlighted={isClassic(game)
                  ? 'green'
                  : word.axis === 'col'
                    ? 'daily-vertical'
                    : 'daily-horizontal'}
              />
            </div>
          {/snippet}
          {#snippet footer()}
            <div>
              <InfiniteLoading
                distance={sizes[dims.size]}
                on:infinite={handleInfinite}
              >
                <Spinner slot="spinner" />
                <div slot="noMore"></div>
                <div slot="noResults"></div>
              </InfiniteLoading>
            </div>
          {/snippet}
        </VirtualList>
      </ScrollShadow>
    {/if}
  {/key}
</div>

<style lang="scss">
  .container {
    position: relative;
    height: 100%;
    overflow: hidden;
  }
  .item-container {
    display: flex;
    align-items: center;
    padding: 0 var(--pad);
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
</style>
